import { GatewayControleQuinquennal } from "@conformite/gateway";
import { isAxiosError } from "axios";
import {
  InfoCard,
  LoadingPage,
  TagWithIcon,
  useDialog,
  Button,
  displayErrorToast,
  displaySucessToast,
} from "@endya-conformite/ui-kit";
import { useAnalyseEstimationQuery } from "@src/data/controle-quinquennal/hook/useAnalyseEstimationQuery";
import {
  ANALYSE_COST_BLOCKED_MESSAGE,
  getAnalyseCostBlockedMessage,
  isAnalyseCostBlockedError,
} from "@src/helper/analyseEstimation.helper";
import { useCQAxeWithFilesPaginatedQuery } from "@src/data/controle-quinquennal/hook/useCQAxeWithFilesQuery";
import { useEntreprise } from "@src/data/entreprise/hook/useEntreprise";
import { useAssujettiListQuery } from "@src/data/assujetti/hook/useAssujettiListQuery";
import { usePersonneContext } from "@src/context/PersonneContext";
import CheckIcon from "@endya-conformite/ui-kit/src/assets/icons/check-circle-outline.svg?react";
import SyncProblem from "@endya-conformite/ui-kit/src/assets/icons/sync-problem.svg?react";
import Refresh from "@endya-conformite/ui-kit/src/assets/icons/refresh.svg?react";
import { AxePageTemplate } from "@src/components/templates/AxePageTemplate";
import { ConnectedPageTemplate } from "@src/components/templates/ConnectedPageTemplate";
import { useIsQuestionnaireComplete } from "@src/components/questionnaire/QuestionnaireForm/useIsQuestionnaireComplete";
import { useQuestionnaireLatestInstanceQuery } from "@src/data/questionnaire/hook/useQuestionnaireLatestInstanceQuery";
import { useQuestionnaireDefinitionQuery } from "@src/data/questionnaire/hook/useQuestionnaireDefinitionQuery";
import {
  getRemediationStatus,
  getRemediationStatusFromAnswers,
} from "@src/helper/remediation.helper";
import { useIsAssujettiAxeComplete } from "@src/pages/controle-quinquennal/useIsAssujettiAxeComplete";
import { useCQWithAxesQuery } from "@src/data/controle-quinquennal/hook/useCQWithAxesQuery";
import { useCQAxeRemediationsQuery } from "@src/data/controle-quinquennal/hook/useCQAxeRemediationsQuery";
import { useValidateAxeMutation } from "@src/data/controle-quinquennal/hook/useValidateAxeMutation";
import { useRelaunchAxeMutation } from "@src/data/controle-quinquennal/hook/useRelaunchAxeMutation";
import { isRecette } from "@src/helper/environnement.helper";
import {
  isControleQuinquennalReadOnly,
  TicketStatus,
  VERIFICATION_STATUS_MAP,
  VerificationStatus,
} from "@src/helper/ticket.helper";
import { formatTitle } from "@src/helper/title.helper";
import {
  axeTypeToLabel,
  axeTypeToQuestionnaireSlug,
  CQParams,
  cqDossierUrl,
} from "@src/pages/controle-quinquennal/controleQuinquennal.helper";
import { controleQuinquennalKeys } from "@src/data/controle-quinquennal/hook/controle-quinquennal.keys";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FIL_ARIANE_MES_CONTROLES } from "@src/pages/controle-quinquennal/cqListe.wording";
import { AxeBody } from "./content/AxeBody";
import { AnalyseEstimationMessage } from "./components/AnalyseEstimationMessage";
import {
  CQRemediationDialog,
  CQRemediationDialogHandle,
} from "./components/CQRemediationDialog";
import { CQRemediationFormValues } from "./components/CQRemediationForm";
import { REMEDIATION_TAB_CREATED } from "./CQRemediationPage";
import styles from "./CQAxePage.module.scss";

function AxeSubheader({
  axeName,
  isAssujetti,
  millesime,
  siren,
  processingStatus,
}: {
  axeName: string;
  isAssujetti: boolean;
  millesime: number;
  siren: string;
  processingStatus?: GatewayControleQuinquennal.VerificationStatus | null;
}) {
  const { selectedPersonneId } = usePersonneContext();
  const { data: assujettis } = useAssujettiListQuery(
    millesime,
    siren,
    isAssujetti
  );
  const selectedAssujetti = isAssujetti
    ? (assujettis?.find((assujetti) => assujetti.id === selectedPersonneId) ??
      assujettis?.[0])
    : undefined;
  const title = selectedAssujetti
    ? `${axeName} — ${selectedAssujetti.surname} ${selectedAssujetti.name}`
    : axeName;
  const processingConfig =
    processingStatus && processingStatus !== VerificationStatus.SUCCESS
      ? VERIFICATION_STATUS_MAP[processingStatus]
      : undefined;

  return (
    <div className={styles.subheader}>
      <div className={styles.title}>
        {title && <h2 className={styles.axeName}>{title}</h2>}
        <div className={styles.titleActions}>
          {isRecette && processingStatus === VerificationStatus.PENDING && (
            <span className={styles.analysisInProgress}>
              <Refresh />
              Analyse en cours…
            </span>
          )}
          {isRecette &&
            processingConfig &&
            processingStatus !== VerificationStatus.PENDING && (
              <TagWithIcon
                variant={processingConfig.variant}
                overrideIcon={processingConfig.icon}
              >
                {processingConfig.label}
              </TagWithIcon>
            )}
        </div>
      </div>
    </div>
  );
}

export function CQAxePageContent({ idEntreprise, idCQ, idAxe }: CQParams) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: cq } = useCQWithAxesQuery(idEntreprise);
  const { data: queryResult } = useCQAxeWithFilesPaginatedQuery(idCQ, idAxe);
  const { data: entreprise } = useEntreprise(cq?.millesime, idEntreprise);
  const { openFloatingDialog, openConfirmDialog } = useDialog();
  const { mutate: validateAxe, isPending: isValidating } =
    useValidateAxeMutation(idEntreprise);
  const { mutate: relaunchAxe, isPending: isRelaunching } =
    useRelaunchAxeMutation(idEntreprise);
  const { data: estimation } = useAnalyseEstimationQuery(idCQ, {
    scope: "AXE",
    idAxe,
  });

  const liveAxe = cq?.controleQuinquennalAxes?.find(
    (axe) => axe.idAxe === idAxe
  );
  const isAssujettiAxe =
    liveAxe?.type ===
    GatewayControleQuinquennal.ControleQuinquennalAxeType.ASSUJETTI;
  const questionnaireSlug = liveAxe?.type
    ? axeTypeToQuestionnaireSlug[liveAxe.type]
    : undefined;
  const { isComplete: isQuestionnaireComplete } = useIsQuestionnaireComplete(
    questionnaireSlug,
    "CONTROLE_QUINQUENNAL_AXE",
    idAxe
  );
  const {
    isComplete: isAssujettiComplete,
    isEmpty: isAssujettiAxeEmpty,
    isRemediationRequired: isAssujettiRemediationRequired,
  } = useIsAssujettiAxeComplete({
    enabled: isAssujettiAxe,
    millesime: cq?.millesime,
    siren: entreprise?.siren,
    entreprise,
  });

  const { data: latestInstance } = useQuestionnaireLatestInstanceQuery(
    "CONTROLE_QUINQUENNAL_AXE",
    idAxe,
    questionnaireSlug
  );
  const { data: questionnaireDefinition } =
    useQuestionnaireDefinitionQuery(questionnaireSlug);
  // Réponses en cours dans le formulaire (avant enregistrement) pour que les
  // boutons réagissent immédiatement au choix du vérificateur.
  const [liveAnswers, setLiveAnswers] = useState<Record<string, string> | null>(
    null
  );
  const [isQuestionnaireDirty, setIsQuestionnaireDirty] = useState(false);
  // Ignore les nouvelles références identiques pour éviter une boucle de rendu.
  const handleAnswersChange = useCallback(
    (nextAnswers: Record<string, string>) => {
      setLiveAnswers((prev) => {
        if (
          prev &&
          Object.keys(prev).length === Object.keys(nextAnswers).length &&
          Object.keys(nextAnswers).every(
            (key) => prev[key] === nextAnswers[key]
          )
        ) {
          return prev;
        }
        return nextAnswers;
      });
    },
    []
  );

  const remediationStatus = liveAnswers
    ? getRemediationStatusFromAnswers(questionnaireDefinition, liveAnswers)
    : getRemediationStatus(latestInstance);
  const isRemediationRequired = isAssujettiAxe
    ? isAssujettiRemediationRequired
    : remediationStatus === "OUI" || remediationStatus === "MANQUANT";

  const isCurrentAxeInRemediation =
    liveAxe?.status ===
    GatewayControleQuinquennal.ControleQuinquennalAxeStatus.REMEDIE;
  const { data: remediations, isLoading: isRemediationLoading } =
    useCQAxeRemediationsQuery(idCQ, idAxe, {
      enabled: isCurrentAxeInRemediation,
    });

  const closeDialogRef = useRef<((created: boolean) => void) | null>(null);
  const remediationDialogRef = useRef<CQRemediationDialogHandle>(null);
  const isRemediationDialogOpenRef = useRef(false);
  const pendingNavigationRef = useRef<string | null>(null);
  const detachedDraftRef = useRef<CQRemediationFormValues | undefined>(
    undefined
  );

  const safeNavigate = (route: string) => {
    if (remediationDialogRef.current?.isShowingWarning?.()) return;
    if (remediationDialogRef.current?.isDirty?.()) {
      pendingNavigationRef.current = route;
      remediationDialogRef.current?.triggerDiscardWarning("navigation");
      return;
    }
    navigate(route);
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as { type?: string } | null | undefined;
      if (!data || data.type !== REMEDIATION_TAB_CREATED) return;
      void queryClient.invalidateQueries({
        queryKey: controleQuinquennalKeys.all,
      });
      navigate(cqDossierUrl(idEntreprise, idCQ));
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [idEntreprise, idCQ, navigate, queryClient]);

  useEffect(() => {
    return () => {
      void queryClient.invalidateQueries({
        queryKey: controleQuinquennalKeys.detail(idEntreprise),
      });
    };
  }, [idEntreprise, queryClient]);

  if (!entreprise || !queryResult?.axe || !cq)
    return (
      <ConnectedPageTemplate isAxe>
        <LoadingPage withText={false} />
      </ConnectedPageTemplate>
    );

  const formattedTitle = formatTitle(entreprise.raisonSociale, cq.millesime);

  const axeName = queryResult.axe?.type
    ? axeTypeToLabel[queryResult.axe.type]
    : "";

  const { processingStatus } = queryResult.axe;
  const { analysisError } = queryResult.axe;
  const { canRelaunchAnalysis } = queryResult.axe;

  const liveStatus = liveAxe?.status;
  const isReadOnly = isControleQuinquennalReadOnly(cq.pipelineStage);

  const currentRemediation = isCurrentAxeInRemediation
    ? remediations?.[0]
    : undefined;
  const remediationInitialValues = currentRemediation
    ? {
        description: currentRemediation.description,
        files: currentRemediation.files.map((file) => ({
          idFile: file.idFile,
          pathname: file.pathname,
          filename: file.filename,
        })),
      }
    : undefined;

  const breadcrumbElements = [
    FIL_ARIANE_MES_CONTROLES,
    {
      label: formattedTitle,
      value: cqDossierUrl(idEntreprise, idCQ),
    },
    { label: axeName, value: "current" },
  ];

  const isAxeIncomplete = isAssujettiAxe
    ? !isAssujettiComplete && !isAssujettiAxeEmpty
    : !isQuestionnaireComplete;
  const isCQEditable =
    cq.pipelineStage === TicketStatus.A_TRAITER ||
    cq.pipelineStage === TicketStatus.EN_COURS ||
    cq.pipelineStage === TicketStatus.PIECES_COMPLEMENTAIRES ||
    cq.pipelineStage === TicketStatus.PRE_COMMISSION;
  const isRemediationDisabled = isCurrentAxeInRemediation
    ? isRemediationLoading || !isCQEditable
    : isAxeIncomplete;

  const isValidateDisabled =
    liveStatus ===
      GatewayControleQuinquennal.ControleQuinquennalAxeStatus.VALIDE ||
    (isAssujettiAxe ? !isAssujettiComplete : !isQuestionnaireComplete) ||
    isRemediationRequired ||
    (Boolean(questionnaireSlug) && isQuestionnaireDirty);

  const handleOpenRemediationDialog = () => {
    if (isRemediationDialogOpenRef.current) {
      remediationDialogRef.current?.expand();
      return;
    }
    isRemediationDialogOpenRef.current = true;
    const initialValues = detachedDraftRef.current ?? remediationInitialValues;
    void openFloatingDialog<boolean, Record<never, never>>(
      ({ onClose: closeDialog, dialogRef }) => {
        closeDialogRef.current = closeDialog;
        return (
          <CQRemediationDialog
            ref={remediationDialogRef}
            dialogRef={dialogRef}
            onClose={(created, detachedValues) => {
              isRemediationDialogOpenRef.current = false;
              detachedDraftRef.current = detachedValues;
              const pending = pendingNavigationRef.current;
              pendingNavigationRef.current = null;
              closeDialog(created);
              if (created) {
                navigate(cqDossierUrl(idEntreprise, idCQ));
                return;
              }
              if (pending) navigate(pending);
            }}
            idCQ={idCQ}
            idAxe={idAxe}
            idEntreprise={idEntreprise}
            idRemediation={currentRemediation?.id}
            initialValues={initialValues}
          />
        );
      }
    );
  };

  const handleValidateAxe = () => {
    openConfirmDialog({
      title: `Validation de l'axe ${axeName}`,
      message:
        "Êtes-vous sûr de vouloir marquer cet axe comme validé ? Vous pourrez toujours modifier votre saisie en revenant sur le questionnaire.",
      cancelText: "Annuler",
      confirmText: "Valider",
    })
      .then((shouldValidate) => {
        if (!shouldValidate) return;
        validateAxe(
          { idControleQuinquennal: idCQ, idAxe },
          {
            onSuccess: () => {
              displaySucessToast("L'axe a été validé avec succès");
              safeNavigate(cqDossierUrl(idEntreprise, idCQ));
            },
            onError: () => {
              displayErrorToast(
                "Une erreur inconnue est survenue lors de la validation de la conformité"
              );
            },
          }
        );
      })
      .catch(() => {});
  };

  const handleRelaunchAnalysis = () => {
    const costBlockedMessage = getAnalyseCostBlockedMessage(estimation);
    if (costBlockedMessage) {
      displayErrorToast(costBlockedMessage);
      return;
    }
    openConfirmDialog({
      title: `Relancer l'analyse de l'axe ${axeName}`,
      message: (
        <AnalyseEstimationMessage
          message="Relancer l'analyse IA de cet axe ? Les réponses IA actuelles seront recalculées."
          estimation={estimation}
        />
      ),
      cancelText: "Annuler",
      confirmText: "Relancer",
    })
      .then((shouldRelaunch) => {
        if (!shouldRelaunch) return;
        relaunchAxe(
          { idControleQuinquennal: idCQ, idAxe },
          {
            onSuccess: () => {
              displaySucessToast(
                "Analyse relancée. Le statut se met à jour automatiquement."
              );
            },
            onError: (error) => {
              if (isAnalyseCostBlockedError(error)) {
                displayErrorToast(ANALYSE_COST_BLOCKED_MESSAGE);
                return;
              }
              // 409 = analyse fraîche déjà en cours (un PENDING périmé, lui, est accepté).
              if (isAxiosError(error) && error.response?.status === 409) {
                displayErrorToast(
                  "Une analyse est déjà en cours pour cet axe. Réessayez une fois qu'elle est terminée."
                );
                return;
              }
              displayErrorToast("Impossible de relancer l'analyse de cet axe.");
            },
          }
        );
      })
      .catch(() => {});
  };

  return (
    <ConnectedPageTemplate
      isAxe
      breadcrumb={{
        items: breadcrumbElements,
        setSelected: (nextRoute) => {
          const currentRoute = breadcrumbElements.at(-1);
          if (currentRoute && nextRoute !== currentRoute.value) {
            safeNavigate(nextRoute);
          }
        },
        selected: breadcrumbElements[2].value,
      }}
    >
      <AxePageTemplate
        actions={
          isReadOnly ? undefined : (
            <div className={styles.actionsContainer}>
              {isRecette && (
                <div className={styles.analysisActions}>
                  <Button
                    variant="outline-accent"
                    onClick={handleRelaunchAnalysis}
                    iconVariant="right"
                    icon={<Refresh />}
                    // Actif uniquement quand une relance a un sens (analyse échouée,
                    // orpheline, ou pièce vérificateur ajoutée depuis) : le serveur
                    // tranche via `canRelaunchAnalysis`. Une analyse fraîche ou réussie
                    // sans nouvelle pièce reste verrouillée — relancer coûte des jetons IA.
                    disabled={isRelaunching || !canRelaunchAnalysis}
                  >
                    Relancer l&apos;analyse IA
                  </Button>
                  {processingStatus === VerificationStatus.FAILED &&
                    analysisError && (
                      <InfoCard type="error" className={styles.analysisError}>
                        {analysisError}
                      </InfoCard>
                    )}
                </div>
              )}
              <div>
                <Button
                  variant="outline-accent"
                  className={styles.remediationButton}
                  onClick={handleOpenRemediationDialog}
                  iconVariant="right"
                  icon={<SyncProblem />}
                  disabled={isRemediationDisabled}
                >
                  {isCurrentAxeInRemediation
                    ? "Modifier la remédiation"
                    : "Remédiation"}
                </Button>
                <Button
                  icon={<CheckIcon />}
                  iconVariant="right"
                  onClick={handleValidateAxe}
                  disabled={isValidating || isValidateDisabled}
                >
                  Valider l&apos;axe
                </Button>
              </div>
            </div>
          )
        }
      >
        <AxeSubheader
          axeName={axeName}
          isAssujetti={isAssujettiAxe}
          millesime={cq.millesime}
          siren={entreprise.siren}
          processingStatus={processingStatus}
        />
        <AxeBody
          onQuestionnaireAnswersChange={handleAnswersChange}
          onQuestionnaireDirtyChange={setIsQuestionnaireDirty}
        />
      </AxePageTemplate>
    </ConnectedPageTemplate>
  );
}

export function CQAxePage() {
  const { idEntreprise, idCQ, idAxe } = useParams<CQParams>();
  if (!idEntreprise || !idCQ || !idAxe)
    return <div>Impossible d&apos;afficher la page</div>;
  return (
    <CQAxePageContent
      key={idAxe}
      idEntreprise={idEntreprise}
      idCQ={idCQ}
      idAxe={idAxe}
    />
  );
}
